import { ICallTrackingLogCalls, ICallTrackingNumbers } from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import dayjs from 'dayjs';
import {
  Between,
  Equal,
  IQueryCommandOptions,
  IWhereCommand,
  In,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import {
  IExportFilterPayloadV2,
  IFilterPayloadV2,
  buildCallLogStatusAggregationWhere,
  getCallLogImpliedDbStatuses,
  getCallLogStatusFilterValues,
  isFlatWhereCommand,
  resolveCallLogStatusWhere,
} from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { ICallTrackingLogStats } from '@/shared/interfaces/call-tracking-log-stats';
import { ICallTrackingLogCallsState } from '@/shared/interfaces/call-tracking-log-calls-store';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { TRootState } from '..';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

// Base store configuration
const adminGatewayResourcePath: string = '/call-tracking-api/call_tracking_log_calls';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  relations: [
    { path: 'partner', alias: 'partner' },
    { path: 'building', alias: 'building' },
    { path: 'building.city', alias: 'city' },
    { path: 'city.province', alias: 'province' },
    { path: 'interactiveVoiceResponse', alias: 'interactiveVoiceResponse' },
  ],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ICallTrackingNumbers>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'call-tracking-log-calls/'

  state: {
    ...base.state,
    logStats: {
      countCalls: 0,
      countMessages: 0,
      avgDuration: 0,
    } as ICallTrackingLogCallsState,
  },

  getters: {
    ...base.getters,
    logStats(state: ICallTrackingLogCallsState) {
      return state.logStats;
    },
  },

  mutations: {
    ...base.mutations,
    setLogStats: (state: ICallTrackingLogCallsState, payload: ICallTrackingLogStats) => {
      state.logStats = payload;
    },
  },

  actions: {
    ...base.actions,

    async getCallLogStats(ctx: ActionContext<ICallTrackingLogCallsState, TRootState>, payload: IFilterPayloadV2) {
      const rawWhere = payload.filters?.where ?? [];
      const flatWhere = rawWhere.filter(isFlatWhereCommand);
      const timeRange = flatWhere.filter((command) => command.field === 'timestamp' || command.field === 'created');
      const partnerKeys = flatWhere.find((command) => command.field === 'p.partnerKey')?.value.split('|') ?? [];
      const statusFilterValues = getCallLogStatusFilterValues(rawWhere);

      const fromDate = (
        timeRange.length !== 0
          ? timeRange.find((w) => w.operator === 'gte')?.value
          : dayjs().startOf('year').format('YYYY-MM-DD')
      );

      const toDate = (
        timeRange.length !== 0 && (timeRange.find((w) => w.operator === 'lte'))
          ? timeRange.find((w) => w.operator === 'lte')?.value
          : dayjs().add(1, 'day').format('YYYY-MM-DD')
      );

      const aggregateRelations = [
        { path: 'partner', alias: 'p' },
      ];

      // Constraints every aggregation, and every OR branch inside it, has to keep.
      const baseWheres: IWhereCommand[] = [
        Equal('clientId', ctx.rootGetters['users/currentClient'].id),
        Between('timestamp', fromDate, toDate),
        ...(partnerKeys.length ? [In('p.partnerKey', partnerKeys)] : []),
      ];
      const callsWhere = buildCallLogStatusAggregationWhere(baseWheres, statusFilterValues);
      // Messages have no `answeredBy` column, so they only get the plain status filter.
      const messageStatuses = getCallLogImpliedDbStatuses(statusFilterValues);
      const messagesWhere = [...baseWheres, ...(messageStatuses.length ? [In('status', messageStatuses)] : [])];

      // A copy rather than writes into `payload.filters`, which the page keeps reusing for its own requests.
      const statFilters: IQueryCommandOptions = {
        ...(payload.filters ?? {}),
        relations: defaultGetListFilters.relations,
        // Resolved like the list request, so the row the aggregates are read from is not filtered out by the
        // synthetic machine-completed status.
        where: resolveCallLogStatusWhere(rawWhere),
        alias: 'cl',
        select: ['countCalls', 'countMessages', 'avgDuration'],
        aggregations: [
          {
            table: 'call_tracking_log_calls',
            propertyAlias: 'countCalls',
            whereCommands: [...callsWhere],
            relations: aggregateRelations,
            column: 'id',
            aggregate: 'COUNT_DISTINCT',
          },
          {
            table: 'call_tracking_log_messages',
            propertyAlias: 'countMessages',
            whereCommands: messagesWhere,
            relations: aggregateRelations,
            column: 'id',
            aggregate: 'COUNT_DISTINCT',
          },
          {
            table: 'call_tracking_log_calls',
            propertyAlias: 'avgDuration',
            whereCommands: [...callsWhere],
            relations: aggregateRelations,
            column: 'dialDuration',
            aggregate: 'AVG',
          },
        ],
        // Ensure only one result is returned.
        limit: 1,
      };

      const response = await adminRequest.get(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(statFilters)}`);

      ctx.commit('setLogStats', {
        countCalls: response.data[0] ? response.data[0].countCalls : 0,
        countMessages: response.data[0] ? response.data[0].countMessages : 0,
        avgDuration: response.data[0] ? response.data[0].avgDuration : 0,
      });
    },

    async getCallTrackingLogsDataCSV(ctx: IAction<IStateStrict<ICallTrackingLogCalls>>, payload: IExportFilterPayloadV2) {
      const url = adminGatewayResourcePath + convertQueryCommandToQueryString(payload.filters);
      const response = await adminRequest.post(`${url}&csvExport=true`, { fields: payload.fields });
      downloadCSV(typeof response === 'string' ? response : '', payload.filename || `${dayjs().format('DD-MM-YYYY')}_report.csv`);
    },
  },
};
